import { useState } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useClerk } from "@clerk/react";
import {
  LayoutGrid, Building2, Handshake, Users, Wallet, FileScan, ShieldCheck, ScrollText, BellRing, MapPin, CalendarCheck,
  BarChart3, FileText, Settings, MoreHorizontal, X, LogOut, UserCircle2, UsersRound, Globe, ClipboardCheck, Gauge, Eye, Sparkles, Footprints, Smartphone, Scale, TrendingUp, Factory, PieChart, Timer,
} from "lucide-react";
import { InstallDialog } from "./InstallApp";
import { useInstall } from "../lib/install";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { useMember } from "../lib/member";
import { OfflineQueue, useNet } from "./OfflineQueue";
import { cacheClear } from "../lib/idb";
import { listQueue, clearQueue } from "../lib/offline";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";

const NAV = [
  ["/", "Dashboard", LayoutGrid, ["admin", "staff", "vendor", "employer"]],
  ["/assistant", "AI Assistant", Sparkles, ["admin", "staff", "vendor", "employer"]],
  ["/pe", "PE Filings", ClipboardCheck, ["admin", "staff", "employer"]],
  ["/my-score", "My Score", Gauge, ["vendor"]],
  ["/my-filings", "My Filings", ClipboardCheck, ["vendor"]],
  ["/sites", "Sites", MapPin, ["admin", "staff", "vendor", "employer"]],
  ["/employers", "Principal Employers", Building2, ["admin", "staff"]],
  ["/vendors", "Vendor Partners", Handshake, ["admin", "staff", "employer"]],
  ["/workers", "Workers", Users, ["admin", "staff", "vendor", "employer"]],
  ["/attendance", "Attendance", CalendarCheck, ["admin", "staff", "vendor", "employer"]],
  ["/wages", "Payroll", Wallet, ["admin", "staff", "vendor", "employer"]],
  ["/challans", "Challan Proofs", FileScan, ["admin", "staff", "vendor", "employer"]],
  ["/compliance", "Payment Holds", ShieldCheck, ["admin", "staff", "employer"]],
  ["/reports", "Reports", BarChart3, ["admin", "staff"]],
  ["/documents", "Documents", FileText, ["admin", "staff", "vendor"]],
  ["/raises", "Wage Raises", TrendingUp, ["admin", "vendor", "employer"]],
  ["/production", "Production & Invoices", Factory, ["admin", "staff", "vendor", "employer"]],
  ["/mis", "MIS & Control", PieChart, ["admin", "staff", "vendor", "employer"]],
  ["/labour", "Overtime & Bonus", Timer, ["admin", "staff", "vendor", "employer"]],
  ["/register", "Wage Register", ScrollText, ["admin", "staff", "vendor", "employer"]],
  ["/alerts", "Alerts", BellRing, ["admin", "staff", "employer"]],
  ["/site-visits", "Site Visits", Footprints, ["admin", "staff"]],
  ["/laws", "Laws & Rules", Scale, ["admin", "staff", "vendor", "employer"]],
  ["/settings", "Settings", Settings, ["admin", "staff"]],
  ["/organisation", "Organisation", Building2, ["employer", "vendor"]],
];
const TABS = [["/", "Home", LayoutGrid], ["/workers", "Workers", Users], ["/attendance", "Attendance", CalendarCheck], ["/reports", "Reports", BarChart3]];

const OFFLINE_OK = /^\/(workers(\/[^/]+)?|attendance)\/?$/;
const NeedsSignal = () => (
  <div className="c-surface c-rise p-8 max-w-lg" data-testid="needs-signal">
    <div className="c-eyebrow mb-2">No signal</div>
    <h2 className="text-lg font-semibold mb-2">This page needs signal</h2>
    <p className="text-sm text-slate-400 mb-5">With no signal you can still add workers, take their photos and documents, and mark attendance. Everything uploads when you're back online.</p>
    <div className="flex gap-2">
      <Link to="/workers" className="rounded-md bg-[#00E599] px-4 py-2 text-sm font-semibold text-[#0B1017]" data-testid="needs-signal-workers">Workers</Link>
      <Link to="/attendance" className="rounded-md border border-[#1F2A3E] px-4 py-2 text-sm hover:bg-[#1A2234]" data-testid="needs-signal-attendance">Attendance</Link>
    </div>
  </div>);

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const Brand = () => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
    <div className="h-8 w-8 rounded-lg bg-[#00E599] text-[#0B1017] grid place-items-center font-display font-extrabold text-sm">369</div>
    <div className="font-display font-extrabold text-lg tracking-tight">Comply369</div>
  </Link>
);

const NavItems = ({ items, onClick, t }) => (
  <nav className="grid gap-0.5">
    {items.map(([to, label, Icon]) => (
      <NavLink key={to} to={to} end={to === "/"} onClick={onClick} data-testid={`nav-${slug(label)}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive
          ? "bg-[#00E599]/10 text-[#00E599] border border-[#00E599]/20" : "text-slate-400 border border-transparent hover:bg-[#1A2234] hover:text-white"}`}>
        <Icon size={16} strokeWidth={1.8} />{t(label)}
      </NavLink>
    ))}
  </nav>
);

const LangChips = ({ lang, setLang, LANGS }) => (
  <div className="flex items-center gap-1" data-testid="language-switcher" translate="no">
    <Globe size={14} className="text-slate-500 mr-1" />
    {LANGS.map(([k, l]) => (
      <button key={k} onClick={() => setLang(k)} data-testid={`lang-${k}`}
        className={`px-2 py-1 rounded-md text-xs transition-colors ${lang === k ? "bg-[#00E599]/15 text-[#00E599]" : "text-slate-400 hover:text-white hover:bg-[#1A2234]"}`}>{l}</button>
    ))}
  </div>
);

const SignOutWarning = ({ items, onCancel, onConfirm }) => (
  <AlertDialog open onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent className="bg-[#121824] border-[#1F2A3E] w-[calc(100%-2rem)] rounded-lg" data-testid="signout-warning">
      <AlertDialogHeader>
        <AlertDialogTitle>{items.length} item{items.length > 1 ? "s" : ""} not uploaded yet</AlertDialogTitle>
        <AlertDialogDescription>Signing out deletes these from this phone. Get signal and let them upload first if you need them.</AlertDialogDescription>
      </AlertDialogHeader>
      <ul className="max-h-56 overflow-y-auto grid gap-1.5 rounded-lg border border-[#1F2A3E] bg-[#0B1017] p-3" data-testid="signout-warning-list">
        {items.map((i) => <li key={i.id} className="text-xs text-slate-200 flex justify-between gap-3"><span className="truncate">{i.label}</span>
          <span className="text-slate-500 font-mono shrink-0">{new Date(i.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</span></li>)}
      </ul>
      <AlertDialogFooter>
        <AlertDialogCancel className="border-[#1F2A3E] bg-transparent hover:bg-[#1A2234] hover:text-white" data-testid="signout-cancel">Stay signed in</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-rose-500 text-white hover:bg-rose-600" data-testid="signout-confirm">Delete and sign out</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>);

const UserMenu = ({ me, t, lang, setLang, LANGS }) => {
  const { signOut } = useClerk();
  const nav = useNavigate();
  const app = useInstall();
  const [install, setInstall] = useState(false);
  const [waiting, setWaiting] = useState(null);
  const initials = (me?.name || me?.email || "?").split(/[\s@]/).map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  const leave = async () => { await clearQueue(); await cacheClear(); signOut(); };
  const tryLeave = async () => {
    const items = await listQueue().catch(() => []);
    if (items.length) setWaiting(items); else leave();
  };
  return (<>
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-testid="user-menu-trigger" className="h-9 w-9 rounded-full bg-[#1A2234] border border-[#1F2A3E] grid place-items-center text-xs font-bold hover:border-[#00E599]/50">{initials}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-[#121824] border-[#1F2A3E]">
        <DropdownMenuLabel>
          <div className="font-semibold" data-testid="user-menu-name">{me?.name}</div>
          <div className="text-xs text-slate-400 font-normal">{me?.email}</div>
          <span className="mt-2 inline-block rounded-md border border-[#00E599]/30 bg-[#00E599]/10 px-2 py-0.5 text-[10px] font-mono uppercase text-[#00E599]" data-testid="user-role-badge">
            {me?.role === "admin" ? "company admin" : me?.role}{me?.vendor_name ? ` · ${me.vendor_name}` : ""}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {!["vendor", "employer"].includes(me?.role) && <DropdownMenuItem onClick={() => nav("/settings?tab=profile")} data-testid="menu-my-profile"><UserCircle2 size={14} className="mr-2" />{t("My Profile")}</DropdownMenuItem>}
        {me?.role === "admin" && <DropdownMenuItem onClick={() => nav("/settings?tab=users")} data-testid="menu-manage-users"><UsersRound size={14} className="mr-2" />{t("Manage Users")}</DropdownMenuItem>}
        {!app.installed && <DropdownMenuItem onClick={() => setInstall(true)} data-testid="menu-install-app"><Smartphone size={14} className="mr-2" />Install app</DropdownMenuItem>}
        <div className="md:hidden px-2 py-2 grid grid-cols-3 gap-1">
          {LANGS.map(([k, l]) => <button key={k} onClick={() => setLang(k)} data-testid={`menu-lang-${k}`} className={`text-xs rounded px-1 py-1 ${lang === k ? "text-[#00E599]" : "text-slate-400"}`}>{l}</button>)}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={tryLeave} data-testid="menu-sign-out" className="text-rose-400"><LogOut size={14} className="mr-2" />{t("Sign Out")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    {install && <InstallDialog onClose={() => setInstall(false)} />}
    {waiting && <SignOutWarning items={waiting} onCancel={() => setWaiting(null)} onConfirm={leave} />}
  </>);
};

export default function AppShell() {
  const { me, t, lang, setLang, LANGS } = useMember();
  const [more, setMore] = useState(false);
  const { pathname } = useLocation();
  const net = useNet();
  const items = NAV.filter((n) => n[3].includes(me?.role));
  const base = `/${pathname.split("/")[1]}`;
  const known = NAV.find((n) => n[0] === base);
  if (known && !known[3].includes(me?.role)) return <Navigate to={me?.role === "employer" ? "/pe" : "/"} replace />;
  const pe = me?.role === "employer";
  return (
    <div className="min-h-screen flex">
      <aside className="no-print hidden lg:flex w-60 shrink-0 flex-col gap-7 border-r border-[#1F2A3E] bg-[#0B1017] p-5 sticky top-0 h-screen overflow-y-auto">
        <Brand /><NavItems items={items} t={t} />
      </aside>
      <div className="flex-1 min-w-0 pb-20 lg:pb-0">
        <header className="no-print sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-[#1F2A3E] bg-[#0B1017]/85 backdrop-blur-xl px-4 lg:px-10 py-3">
          <div className="lg:hidden"><Brand /></div>
          <span className="hidden lg:inline-flex items-center rounded-full border border-[#1F2A3E] bg-[#121824] px-3 py-1 text-xs" data-testid="company-pill">
            <Building2 size={12} className="mr-1.5 text-[#00E599]" />{me?.company?.name}</span>
          <div className="flex items-center gap-3">
            <OfflineQueue />
            <div className="hidden md:block"><LangChips lang={lang} setLang={setLang} LANGS={LANGS} /></div>
            <UserMenu me={me} t={t} lang={lang} setLang={setLang} LANGS={LANGS} />
          </div>
        </header>
        <main className={`print-area p-4 sm:p-6 lg:p-10 max-w-[1600px] mx-auto ${pe ? "pe-readonly" : ""}`}>
          {pe && <div className="no-print mb-5 flex items-center gap-2 rounded-lg border border-cyan-500/25 bg-cyan-500/5 px-4 py-2.5 text-xs text-cyan-100" data-testid="pe-view-only-banner">
            <Eye size={14} className="text-cyan-300 shrink-0" />Principal employer view — you can see all contractors under {me?.vendor_name || "your company"}, approve CLRA renewals and release payment holds. Contractor data can only be changed by the company team.</div>}
          {net.offline && !OFFLINE_OK.test(pathname) ? <NeedsSignal /> : <Outlet />}</main>
      </div>
      <nav className="no-print lg:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-5 border-t border-[#1F2A3E] bg-[#0B1017]/95 backdrop-blur-xl" data-testid="mobile-tab-bar">
        {TABS.filter(([to]) => items.some((i) => i[0] === to) || to === "/").map(([to, label, Icon]) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`tab-${slug(label)}`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] ${isActive ? "text-[#00E599]" : "text-slate-400"}`}>
            <Icon size={18} />{t(label)}</NavLink>
        ))}
        <button onClick={() => setMore(true)} data-testid="tab-more" className="flex flex-col items-center gap-1 py-2.5 text-[10px] text-slate-400"><MoreHorizontal size={18} />{t("More")}</button>
      </nav>
      {more && (
        <div className="no-print fixed inset-0 z-50 bg-black/70 backdrop-blur-md lg:hidden" onClick={() => setMore(false)}>
          <div className="absolute bottom-0 inset-x-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-[#0B1017] border-t border-[#1F2A3E] p-5" onClick={(e) => e.stopPropagation()} data-testid="more-sheet">
            <div className="flex justify-between items-center mb-4"><Brand /><button onClick={() => setMore(false)} data-testid="more-sheet-close"><X size={20} /></button></div>
            <NavItems items={items} t={t} onClick={() => setMore(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
